/**
 * Composable for logging operator actions to Django
 * All UI actions are logged for debugging and analytics
 * 
 * FULL LOGGING: Every action, every state, every click!
 */

import { useAuthStore } from '@/stores/auth'
import { getIsOnline } from '@/composables/useNetworkHealth'

const API_URL = '/api/v1/dialer/operator-action-log/'

/**
 * Get browser/tab state for debugging
 */
function getBrowserState() {
  return {
    // Tab visibility
    tab_hidden: document.hidden,
    tab_visibility: document.visibilityState,
    
    // Window state
    window_focused: document.hasFocus(),
    window_width: window.innerWidth,
    window_height: window.innerHeight,
    
    // Time
    client_time: new Date().toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    
    // Browser info
    user_agent: navigator.userAgent?.slice(0, 150),
    language: navigator.language,
    online: navigator.onLine,
    
    // Memory (if available)
    memory_used: navigator.deviceMemory ? `${navigator.deviceMemory}GB` : 'unknown'
  }
}

/**
 * Log operator action to Django
 * @param {string} eventType - Type of event (page_loaded, accept_clicked, etc.)
 * @param {object} data - Additional data (call_uuid, call_phone, error_message, extra_data)
 */
async function logAction(eventType, data = {}) {
  try {
    if (!getIsOnline()) return
    const authStore = useAuthStore()
    if (!authStore.accessToken) return

    const payload = {
      event_type: eventType,
      call_uuid: data.callUuid || data.call_uuid || data.uuid,
      call_phone: data.callPhone || data.call_phone || data.phone,
      error_message: data.errorMessage || data.error_message || data.error,
      extra_data: {
        ...getBrowserState(),
        ...(data.extra || data.extraData || {})
      }
    }

    console.log(`[ActionLog] 📝 ${eventType}`, payload.call_uuid || '', payload.call_phone || '', payload.extra_data)

    await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authStore.accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
  } catch (e) {
    // Silent fail - don't break main flow
    console.debug('[ActionLog] Failed to log:', eventType, e.message)
  }
}

// Convenience methods for common events
export function useOperatorActionLog() {
  return {
    // Page events
    pageLoaded: () => logAction('page_loaded'),
    pageClosed: () => logAction('page_closed'),
    pageRefreshed: () => logAction('page_refreshed'),
    
    // WebSocket events
    wsConnected: () => logAction('ws_connected'),
    wsDisconnected: (reason) => logAction('ws_disconnected', { extra: { reason } }),
    wsReconnecting: (attempt) => logAction('ws_reconnecting', { extra: { attempt } }),
    
    // Incoming call events (CRITICAL for debugging!)
    callReceived: (callUuid, callPhone, operatorState) => logAction('call_received', { 
      callUuid, 
      callPhone, 
      extra: { 
        ...operatorState,
        event: 'WebSocket received incoming_call'
      } 
    }),
    
    // Call card events
    callCardShown: (callUuid, callPhone, operatorState) => logAction('call_card_shown', { 
      callUuid, 
      callPhone,
      extra: {
        ...operatorState,
        event: 'Call card rendered in UI'
      }
    }),
    callCardHidden: (callUuid, reason) => logAction('call_card_hidden', { callUuid, extra: { reason } }),
    
    // Ringtone events (CRITICAL for "no sound" debugging!)
    ringtoneAttempt: (callUuid, soundSettings) => logAction('ringtone_attempt', { 
      callUuid, 
      extra: { 
        ...soundSettings,
        event: 'Attempting to play ringtone'
      } 
    }),
    ringtoneStarted: (callUuid) => logAction('ringtone_started', { callUuid }),
    ringtoneBlocked: (callUuid, reason, soundSettings) => logAction('ringtone_blocked', { 
      callUuid, 
      extra: { 
        reason,
        ...soundSettings,
        event: 'Ringtone NOT played'
      } 
    }),
    ringtoneStopped: (callUuid, reason) => logAction('ringtone_stopped', { callUuid, extra: { reason } }),
    
    // Accept/Reject events
    acceptClicked: (callUuid, callPhone) => logAction('accept_clicked', { callUuid, callPhone }),
    acceptSuccess: (callUuid, callPhone) => logAction('accept_success', { callUuid, callPhone }),
    acceptFailed: (callUuid, callPhone, error) => logAction('accept_failed', { callUuid, callPhone, errorMessage: error }),
    rejectClicked: (callUuid, callPhone) => logAction('reject_clicked', { callUuid, callPhone }),

    // [FIX 2026-05-26] Auto-close events for ghost-popup fixes (added so we can audit
    // how often each fix actually fires after deploy)
    //
    // popupAutoClosedDeadCall — fires when ACCEPT click returned a Twilio
    //   "Call is not in-progress / Cannot redirect" style error and we closed
    //   the popup locally (instead of leaving it visible for the operator to
    //   re-click). See DialpadDialog{,V2,V3}.vue.
    popupAutoClosedDeadCall: (callUuid, callPhone, errorReason) => logAction('popup_auto_closed_dead_call', {
      callUuid, callPhone, extra: { error_reason: errorReason }
    }),
    //
    // popupAutoClosedGhost — fires when periodic backend-state poll
    //   (useIncomingCallsWebSocket.startGhostCleanup) detected that a
    //   call still showing "ringing" in the UI is in fact already finished
    //   on the server (status=missed/completed/voicemail/taken_by_other).
    popupAutoClosedGhost: (callUuid, reasonHint, ageSec, takenByName) => logAction('popup_auto_closed_ghost', {
      callUuid, extra: { reason_hint: reasonHint, age_sec: ageSec, taken_by: takenByName }
    }),
    
    // Auto-answer events
    autoAnswerAttempt: (callUuid, callPhone) => logAction('auto_answer_attempt', { callUuid, callPhone }),
    autoAnswerSuccess: (callUuid, callPhone) => logAction('auto_answer_success', { callUuid, callPhone }),
    autoAnswerFailed: (callUuid, callPhone, error) => logAction('auto_answer_failed', { callUuid, callPhone, errorMessage: error }),
    
    // Operator state changes
    acceptingCallsOn: () => logAction('accepting_calls_on'),
    acceptingCallsOff: () => logAction('accepting_calls_off'),
    vertoConnected: () => logAction('verto_connected'),
    vertoDisconnected: (reason) => logAction('verto_disconnected', { extra: { reason } }),
    
    // Call in progress events
    callStarted: (callUuid, callPhone, direction) => logAction('call_started', { callUuid, callPhone, extra: { direction } }),
    callEnded: (callUuid, callPhone, duration, reason) => logAction('call_ended', { callUuid, callPhone, extra: { duration, reason } }),
    
    // Connection events (logged to DB for user connectivity history)
    connectionLost: (data) => logAction('connection_lost', {
      errorMessage: 'Network connection lost',
      extra: {
        offline_at: data.offlineSince ? new Date(data.offlineSince).toISOString() : null,
        recovered_at: data.recoveredAt ? new Date(data.recoveredAt).toISOString() : null,
        duration_seconds: data.durationSec || 0,
        page_url: window.location.pathname
      }
    }),
    connectionRestored: (data) => logAction('connection_restored', {
      extra: {
        offline_at: data.offlineSince ? new Date(data.offlineSince).toISOString() : null,
        recovered_at: data.recoveredAt ? new Date(data.recoveredAt).toISOString() : null,
        duration_seconds: data.durationSec || 0,
        page_url: window.location.pathname
      }
    }),
    
    // Generic log
    log: logAction
  }
}

export default useOperatorActionLog
